import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { deleteCollection } from '@/data/collectionsRepo';
import { useCollections } from '@/hooks/useCollections';
import { useExperiences } from '@/hooks/useExperiences';
import { collectionStatusLabels, type CollectionStatusType, type CollectionType } from '@/types/types_collection';
import { experienceRegionLabels } from '@/types/types_experience';

const statusPillClass: Record<CollectionStatusType, string> = {
  active: 'tw:bg-green-100 tw:text-green-800',
  inactive: 'tw:bg-gray-100 tw:text-gray-800',
  'coming-soon': 'tw:bg-yellow-100 tw:text-yellow-800',
};

/** Admin list of collections, styled to match the Experience Database card grid. */
export function PageCollectionList() {
  const { collections, loading } = useCollections();
  const { experiences } = useExperiences();
  const [toDelete, setToDelete] = useState<CollectionType | null>(null);
  const [deleting, setDeleting] = useState(false);

  const memberCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of experiences) {
      if (e.collectionId) counts.set(e.collectionId, (counts.get(e.collectionId) ?? 0) + 1);
    }
    return counts;
  }, [experiences]);

  async function confirmDelete() {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteCollection(toDelete.id);
      setToDelete(null);
      // The snapshot listener removes the card automatically.
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <header className='tw:mb-6 tw:flex tw:items-center tw:justify-between'>
        <div>
          <h1 className='tw:font-serif tw:text-2xl tw:text-ed-black'>Collections</h1>
          <p className='tw:mt-1 tw:text-sm tw:text-ed-stone'>
            Curated groups of related experiences. {collections.length} collections.
          </p>
        </div>
        <Link
          to='/admin/collections/new'
          className='tw:flex tw:items-center tw:gap-2 tw:bg-ed-gold tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:text-ed-black tw:transition-colors tw:hover:bg-ed-black tw:hover:text-ed-cream'
        >
          <Plus className='tw:size-4' /> Add New Collection
        </Link>
      </header>

      <div className='tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-white tw:p-6 tw:shadow-sm'>
        {loading ? (
          <p className='tw:py-10 tw:text-center tw:text-ed-stone'>Loading…</p>
        ) : collections.length === 0 ? (
          <p className='tw:py-10 tw:text-center tw:text-ed-stone'>No collections yet.</p>
        ) : (
          <div className='tw:grid tw:grid-cols-1 tw:gap-6 tw:md:grid-cols-2 tw:lg:grid-cols-4 tw:xl:grid-cols-5'>
            {collections.map((c) => (
              <CollectionAdminCard
                key={c.id}
                collection={c}
                memberCount={memberCounts.get(c.id) ?? 0}
                onDelete={setToDelete}
              />
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!toDelete} onOpenChange={(open) => !open && !deleting && setToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete collection?</DialogTitle>
            <DialogDescription>
              This will permanently remove &ldquo;{toDelete?.name}&rdquo;. Experiences in this collection will
              become standalone. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' disabled={deleting} onClick={() => setToDelete(null)}>
              Cancel
            </Button>
            <Button variant='destructive' loading={deleting} onClick={() => void confirmDelete()}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CollectionAdminCard({
  collection,
  memberCount,
  onDelete,
}: {
  collection: CollectionType;
  memberCount: number;
  onDelete: (collection: CollectionType) => void;
}) {
  return (
    <div className='tw:flex tw:h-full tw:flex-col tw:overflow-hidden tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-white tw:shadow-sm tw:transition-shadow tw:hover:shadow-md'>
      <div className='tw:relative tw:h-40 tw:shrink-0 tw:bg-ed-umber/10'>
        <img src={collection.heroImage} alt={collection.name} className='tw:h-full tw:w-full tw:object-cover' />
        <div className='tw:absolute tw:top-2 tw:right-2'>
          <span
            className={`tw:rounded-full tw:px-2 tw:py-1 tw:text-xs ${statusPillClass[collection.status]}`}
          >
            {collectionStatusLabels[collection.status]}
          </span>
        </div>
        <div className='tw:absolute tw:bottom-2 tw:left-2'>
          <span className='tw:rounded-sm tw:bg-ed-gold tw:px-2 tw:py-1 tw:text-xs tw:text-ed-black'>
            {memberCount} {memberCount === 1 ? 'experience' : 'experiences'}
          </span>
        </div>
      </div>
      <div className='tw:flex tw:flex-1 tw:flex-col tw:p-4'>
        <p className='tw:mb-1 tw:text-xs tw:tracking-wider tw:text-ed-gold tw:uppercase'>
          {experienceRegionLabels[collection.region]}
        </p>
        <h3 className='tw:mb-1 tw:font-serif tw:text-lg tw:text-ed-black'>{collection.name}</h3>
        <p className='tw:mb-3 tw:text-sm tw:text-ed-stone'>{collection.subtitle}</p>
        <p className='tw:mb-3 tw:line-clamp-2 tw:text-sm tw:text-ed-charcoal/70'>{collection.location}</p>

        <div className='tw:mt-auto tw:flex tw:gap-2 tw:pt-1'>
          <Link
            to={`/admin/collections/${collection.id}/edit`}
            className='tw:flex-1 tw:border tw:border-ed-umber/30 tw:py-2 tw:text-center tw:text-sm tw:text-ed-charcoal tw:transition-colors tw:hover:border-ed-gold tw:hover:text-ed-gold'
          >
            Edit
          </Link>
          <Link
            to={`/collections/${collection.id}`}
            target='_blank'
            className='tw:flex-1 tw:bg-ed-gold tw:py-2 tw:text-center tw:text-sm tw:text-ed-black tw:transition-colors tw:hover:bg-ed-black tw:hover:text-ed-cream'
          >
            View
          </Link>
          <button
            type='button'
            title='Delete'
            onClick={() => onDelete(collection)}
            className='tw:border tw:border-ed-umber/30 tw:px-3 tw:text-ed-charcoal tw:transition-colors tw:hover:border-ed-red tw:hover:text-ed-red'
          >
            <Trash2 className='tw:size-4' />
          </button>
        </div>
      </div>
    </div>
  );
}
